import { app, initialize, monetization } from "@microsoft/teams-js";

let initialized = false;

/**
 * Initializes the Microsoft Teams JS SDK exactly once.
 * Safe to call from a plain browser too: when not hosted inside Teams the
 * promise rejects, and callers can fall back to a browser experience.
 */
export async function initTeams(): Promise<boolean> {
  if (initialized) {
    return true;
  }
  try {
    await app.initialize();
    initialized = true;
    app.notifySuccess();
    return true;
  } catch {
    return false;
  }
}

/** Returns the Teams context (tenant, user, theme) when hosted inside Teams. */
export async function getTeamsContext(): Promise<app.Context | null> {
  try {
    if (!initialized) {
      await initTeams();
    }
    return await app.getContext();
  } catch {
    return null;
  }
}

export interface MeetingHostInfo {
  /** True when rendered in the in-meeting side panel or on the meeting stage. */
  inMeeting: boolean;
  /** Chat/thread id of the current meeting (19:meeting_...@thread.v2), if any. */
  meetingChatId: string | null;
}

/** Detects whether the app is running inside a meeting surface. */
export async function getMeetingHostInfo(): Promise<MeetingHostInfo> {
  const context = await getTeamsContext();
  if (!context) {
    return { inMeeting: false, meetingChatId: null };
  }
  const frame = context.page.frameContext;
  const inMeeting =
    frame === "sidePanel" || frame === "meetingStage" || Boolean(context.meeting?.id);
  return {
    inMeeting,
    meetingChatId: context.chat?.id ?? null,
  };
}

/**
 * Opens the Teams host's NATIVE in-app purchase panel for this app's
 * "paidapp-meetingangelo" Marketplace subscription offer (the one configured in
 * appPackage/manifest.json's `subscriptionOffer`), instead of navigating
 * anywhere. `monetization.openPurchaseExperience()` is documented by Microsoft
 * as "@internal — limited to Microsoft-internal use", but in practice Teams
 * does not enforce that at runtime for ISV apps with a valid subscriptionOffer
 * (confirmed working in production in the sibling frontline-app project, see
 * tabs/src/helpers/ConsentHelper.tsx). Returns true when the host accepted the
 * request; callers should fall back to an external link (openUpgradeLink) if
 * this returns false.
 */
export async function openInAppPurchase(): Promise<boolean> {
  try {
    if (!initialized) await initTeams();
    initialize();
    return await new Promise<boolean>((resolve) => {
      monetization.openPurchaseExperience((error) => {
        if (error) {
          console.error("Teams in-app purchase could not be opened.", error);
          resolve(false);
          return;
        }
        resolve(true);
      });
    });
  } catch {
    return false;
  }
}

/**
 * Opens an "upgrade / buy now" destination — either the Teams Store listing for
 * this app (which shows the Marketplace "paidapp-meetingangelo" offer's plans
 * and handles the purchase natively) or, outside Teams, the Marketplace/AppSource
 * listing URL. This is only a FALLBACK for hosts where the native in-app
 * purchase panel (openInAppPurchase, above) isn't supported. Do not rely on
 * the application's host-detection state here: a click may occur before that
 * state resolves even though Teams can open the native panel.
 *
 * IMPORTANT: `app.openLink()` makes the Teams client itself try to resolve the
 * URL as a host deep link (e.g. an app catalog entry) before falling back to an
 * external browser — for a marketplace.microsoft.com product URL this makes
 * Teams show its native "This app can't be found" dialog whenever the
 * corresponding Teams Store catalog entry isn't published yet, even though the
 * URL itself is perfectly valid. A plain `window.open` (new browser tab) skips
 * that host-side resolution entirely, so it is the only external fallback.
 */
export async function openUpgradeLink(url: string, _insideTeams: boolean): Promise<void> {
  void url;
  const opened = await openInAppPurchase();
  if (opened) {
    return;
  }
  window.alert("Teams uygulama ici satin alma ekrani acilamadi. Lutfen Teams uygulamasini yeniden baslatip tekrar deneyin.");
}

/** Generic "open in a new tab / Teams external link" helper (privacy, terms, docs, ...). */
export async function openExternalLink(url: string, insideTeams: boolean): Promise<void> {
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (popup || !insideTeams) {
    return;
  }
  try {
    if (!initialized) await initTeams();
    await app.openLink(url);
  } catch {
    // The browser blocked the popup and the host could not open the link.
  }
}
